"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MCMCombobox, type ComponentLink } from "@/components/ui/MCMCombobox";
import { ControlsProvider, ControlCombobox } from "@/components/ui/ControlCombobox";
import { AssetsProvider, AssetsCombobox } from "@/components/ui/AssetsCombobox";
import { RisksProvider, RisksCombobox } from "@/components/ui/RisksCombobox";
import { TeamsProvider, TeamsCombobox } from "@/components/ui/TeamsCombobox";
import { FrameworkCombobox, FrameworksProvider } from "@/components/ui/FrameworkCombobox";
import LegalObligationsCombobox, { LegalObligationsProvider } from "@/components/ui/LegalObligationsCombobox";
import DataTypeCombobox from "@/components/ui/DataTypeCombobox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function ModalComponentSelectionMCMExample() {
  const [open, setOpen] = React.useState(true);
  const [links, setLinks] = React.useState<ComponentLink[]>([]);
  const [primaryControls, setPrimaryControls] = React.useState<(string | number)[]>([]);
  const [assetIds, setAssetIds] = React.useState<string[]>([]);
  const [riskIds, setRiskIds] = React.useState<string[]>([]);
  const [teamNames, setTeamNames] = React.useState<string[]>([]);
  const [frameworks, setFrameworks] = React.useState<string[]>([]);
  const [legalIds, setLegalIds] = React.useState<string[]>([]);
  const [dataTypeIds, setDataTypeIds] = React.useState<string[]>([]);

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Component Selection Modal</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl! w-6xl! bg-transparent shadow-none border-0 p-0">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full overflow-hidden shadow-2xl border border-white/10 flex flex-col" style={{ minHeight: "75vh" }}>
            <div className="p-6 pb-4 border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Component Selection</h2>
              <p className="mt-2 text-muted-foreground">Link controls and components across the platform using the MCM selector and domain-specific pickers.</p>
            </div>

            <div className="p-6 space-y-6 flex-1 overflow-y-auto">
              <Card className="bg-white/5 border-white/20">
                <CardHeader>
                  <CardTitle className="text-lg">Master Control (Primary)</CardTitle>
                </CardHeader>
                <CardContent>
                  <ControlsProvider>
                    <ControlCombobox selected={primaryControls} setSelected={setPrimaryControls} fullWidth placeholder="Select primary control(s)" />
                  </ControlsProvider>
                  <div className="mt-2 text-xs text-muted-foreground">Use a primary control to anchor links. The MCM selector below requires a primary when enabled.</div>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/20">
                <CardHeader>
                  <CardTitle className="text-lg">MCM Selector</CardTitle>
                </CardHeader>
                <CardContent>
                  <MCMCombobox
                    selected={links}
                    onChange={setLinks}
                    placeholder="Select components to link..."
                    fullWidth
                    initialMasterControl={primaryControls.length ? { id: String(primaryControls[0]), label: String(primaryControls[0]) } : null}
                    requirePrimary={true}
                    allowedCategories={[
                      "controls",
                      "assets",
                      "suppliers",
                      "risks",
                      "policies",
                      "assuranceTasks",
                      "checklists",
                      "supportingDocs",
                      "legalObligations",
                      "objectives",
                      "audits",
                      "findings",
                      "incidents",
                      "correctiveActions",
                    ]}
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {links.map(l => (
                      <Badge key={l.id} variant="outline" className="text-xs">{l.domain}: {l.title || l.ref}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-white/5 border-white/20">
                  <CardHeader><CardTitle className="text-lg">Assets</CardTitle></CardHeader>
                  <CardContent>
                    <AssetsProvider>
                      <AssetsCombobox selected={assetIds} setSelected={setAssetIds} fullWidth />
                    </AssetsProvider>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/20">
                  <CardHeader><CardTitle className="text-lg">Risks</CardTitle></CardHeader>
                  <CardContent>
                    <RisksProvider>
                      <RisksCombobox selected={riskIds} setSelected={setRiskIds} fullWidth />
                    </RisksProvider>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/20">
                  <CardHeader><CardTitle className="text-lg">Frameworks</CardTitle></CardHeader>
                  <CardContent>
                    <FrameworksProvider>
                      <FrameworkCombobox selected={frameworks} setSelected={setFrameworks} fullWidth />
                    </FrameworksProvider>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/20">
                  <CardHeader><CardTitle className="text-lg">Legal Obligations</CardTitle></CardHeader>
                  <CardContent>
                    <LegalObligationsProvider>
                      <LegalObligationsCombobox selected={legalIds} setSelected={setLegalIds} fullWidth />
                    </LegalObligationsProvider>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/20">
                  <CardHeader><CardTitle className="text-lg">Data Types</CardTitle></CardHeader>
                  <CardContent>
                    <DataTypeCombobox selected={dataTypeIds} setSelected={setDataTypeIds} fullWidth />
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/20">
                  <CardHeader><CardTitle className="text-lg">Teams</CardTitle></CardHeader>
                  <CardContent>
                    <TeamsProvider>
                      <TeamsCombobox selected={teamNames} setSelected={setTeamNames} fullWidth />
                    </TeamsProvider>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="border-top border-white/10 px-6 py-4 bg-gradient-to-t from-primary/5 to-transparent flex justify-between items-center">
              <div className="text-sm text-muted-foreground">{links.length} link(s) selected</div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
                <Button onClick={() => setOpen(false)}>Save</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
